import { secondsToTimeString } from "@circle-react/helpers/dateTimeHelpers";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { Icon } from "@circleco/compass/components/Icon";
import { IconButton } from "@circleco/compass/components/IconButton";
import { Tooltip } from "@circleco/compass/components/Tooltip";
import { Typography } from "@circleco/compass/components/Typography";
import { useWavesurfer } from "@wavesurfer/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import RecordPlugin from "wavesurfer.js/dist/plugins/record.esm.js";

import { t } from "@/i18n-js/instance";

import { MAX_RECORDING_DURATION_MS } from "./constants";

interface RecordingControlsProps {
  onBlob: (blob: Blob) => void;
  onCancel: () => void;
  onError: () => void;
}

const getBackgroundInverseColor = () =>
  getComputedStyle(document.documentElement)
    .getPropertyValue("--color-background-inverse")
    .trim();

export const RecordingControls = ({
  onBlob,
  onCancel,
  onError,
}: RecordingControlsProps) => {
  const waveformRef = useRef<HTMLDivElement>(null);
  const recordRef = useRef<ReturnType<typeof RecordPlugin.create> | null>(null);
  const cancelledRef = useRef(false);
  const [duration, setDuration] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isSubmitHovered, setIsSubmitHovered] = useState(false);

  const wsOptions = useMemo(
    () => ({
      container: waveformRef,
      waveColor: getBackgroundInverseColor(),
      cursorColor: "transparent",
      barWidth: 3,
      barGap: 3,
      barRadius: 4,
      height: 32,
      normalize: true,
    }),
    [],
  );

  const { wavesurfer } = useWavesurfer(wsOptions);

  useEffect(() => {
    const animationFrame = window.requestAnimationFrame(() => {
      setIsVisible(true);
    });

    return () => {
      window.cancelAnimationFrame(animationFrame);
    };
  }, []);

  useEffect(() => {
    if (!wavesurfer) return;

    const record = wavesurfer.registerPlugin(
      RecordPlugin.create({
        scrollingWaveform: true,
        scrollingWaveformWindow: 7,
        renderRecordedAudio: false,
      }),
    );

    recordRef.current = record;

    const unsubProgress = record.on("record-progress", (timeMs: number) => {
      setDuration(Math.floor(timeMs / 1000));
    });

    const unsubEnd = record.on("record-end", (blob: Blob) => {
      if (cancelledRef.current) {
        onCancel();
        return;
      }

      onBlob(blob);
    });

    void record
      .startRecording()
      .then(() => {
        if (!cancelledRef.current) return;

        if (record.isRecording()) {
          record.stopRecording();
        }
        record.destroy();
      })
      .catch(() => {
        if (cancelledRef.current) return;

        onError();
        onCancel();
      });

    const timeout = window.setTimeout(() => {
      if (record.isRecording()) {
        record.stopRecording();
      }
    }, MAX_RECORDING_DURATION_MS);

    return () => {
      window.clearTimeout(timeout);
      cancelledRef.current = true;
      unsubProgress();
      unsubEnd();
      if (record.isRecording()) {
        record.stopRecording();
      }
      record.destroy();
    };
  }, [onBlob, onCancel, onError, wavesurfer]);

  const cancelRecording = useCallback(() => {
    cancelledRef.current = true;
    if (recordRef.current?.isRecording()) {
      recordRef.current.stopRecording();
      return;
    }

    onCancel();
  }, [onCancel]);

  const submitRecording = useCallback(() => {
    if (recordRef.current?.isRecording()) {
      recordRef.current.stopRecording();
    }
  }, []);

  return (
    <div
      className={classNames(
        "flex w-full origin-right items-center gap-3 transition-[opacity,transform] duration-200 ease-out motion-reduce:transition-none",
        {
          "translate-y-0 scale-100 opacity-100": isVisible,
          "translate-y-1 scale-95 opacity-0": !isVisible,
        },
      )}
    >
      <div className="flex shrink-0 items-center gap-3">
        <span
          className="size-3 rounded-full bg-danger motion-safe:animate-pulse"
          aria-hidden
        />
        <Typography
          variant="body-md"
          color="secondary"
          className="whitespace-nowrap tabular-nums"
        >
          {secondsToTimeString(duration)}
        </Typography>
      </div>
      <div
        ref={waveformRef}
        className="min-w-[120px] flex-1 overflow-hidden rounded-md"
      />
      <Tooltip content={t("circle_ai_home.voice.cancel")}>
        <button
          type="button"
          className="bg-secondary hover:bg-tertiary focus-visible:outline-secondary flex size-8 shrink-0 items-center justify-center rounded-md transition-colors duration-150 ease-linear focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
          aria-label={t("circle_ai_home.voice.cancel")}
          onClick={cancelRecording}
        >
          <Icon name="cross-small" size="md" color="primary" aria-hidden />
        </button>
      </Tooltip>
      <Tooltip content={t("circle_ai_home.voice.transcribe")}>
        <IconButton
          icon={isSubmitHovered ? "circle-check" : "record"}
          variant="primary"
          size="sm"
          aria-label={t("circle_ai_home.voice.transcribe")}
          onBlur={() => setIsSubmitHovered(false)}
          onFocus={() => setIsSubmitHovered(true)}
          onMouseEnter={() => setIsSubmitHovered(true)}
          onMouseLeave={() => setIsSubmitHovered(false)}
          onClick={submitRecording}
        />
      </Tooltip>
    </div>
  );
};
